import { useAuth0 } from '@auth0/auth0-react'
import {
  UseMutationOptions,
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { QueryKeys } from '../../types'
import {
  getHealthCallAppointment,
  getHealthCallSummary,
  patchCancelHealthCall,
  postBookHealthCall,
} from './healthCalls'
import { BookHealthCallRequest, HealthCallAppointment, HealthCallSummary } from './types'
import { useHealthCallStore } from '../../../../state/store'

export const useGetHealthCallSummaryQuery = (): UseQueryResult<HealthCallSummary, Error> => {
  const { getAccessTokenSilently } = useAuth0()
  return useQuery({
    queryKey: [QueryKeys.HealthCalls, 'private', 'summary'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return await getHealthCallSummary({ accessToken })
    },
    retry: false,
  })
}

export const useGetHealthCallAppointmentQuery = (
  enabled = true
): UseQueryResult<HealthCallAppointment | null, Error> => {
  const { getAccessTokenSilently } = useAuth0()
  const { setHealthCallAppointment } = useHealthCallStore()

  return useQuery<HealthCallAppointment | null, Error>({
    queryKey: [QueryKeys.HealthCalls, 'private', 'appointment'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      const data = await getHealthCallAppointment({ accessToken })
      setHealthCallAppointment(data)
      return data
    },
    retry: false,
    enabled,
  })
}

export const usePatchCancelHealthCallQuery = (
  options?: UseMutationOptions<Response, Error, string, unknown>
): UseMutationResult<Response, Error, string, unknown> => {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options || {}

  return useMutation<Response, Error, string, unknown>({
    mutationKey: [QueryKeys.HealthCalls, 'private', 'cancel'],
    mutationFn: async (appointmentId) => {
      const accessToken = await getAccessTokenSilently()
      return await patchCancelHealthCall({ accessToken, appointmentId })
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.HealthCalls, 'private', 'appointment'] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.HealthCalls, 'private', 'summary'] })
      onSuccess?.(data, variables, context)
    },
    ...restOptions,
  })
}

export const usePostBookHealthCallQuery = (
  options?: UseMutationOptions<Response, Error, BookHealthCallRequest, unknown>
): UseMutationResult<Response, Error, BookHealthCallRequest, unknown> => {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options || {}

  return useMutation<Response, Error, BookHealthCallRequest, unknown>({
    mutationKey: [QueryKeys.HealthCalls, 'private', 'book'],
    mutationFn: async (bookingData) => {
      const accessToken = await getAccessTokenSilently()
      return await postBookHealthCall({ accessToken, bookingData })
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.HealthCalls, 'private', 'appointment'] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.HealthCalls, 'private', 'summary'] })
      onSuccess?.(data, variables, context)
    },
    ...restOptions,
  })
}
